import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

const SpinnerElement = styled.div`
  position: relative;
  display: block;

  &::before {
    content: '';
    display: block;
    position: relative;
    width: 40px;
    height: 40px;

    border-style: solid;
    border-width: 3px;
    border-color: currentColor transparent transparent transparent;

    animation-name: SpinnerAnimation;
    animation-duration: 0.6s;
    animation-iteration-count: infinite;
    animation-timing-function: linear;
    animation-fill-mode: both;

    border-radius: 40px;
  }

  @keyframes SpinnerAnimation {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(360deg);
    }
  }
`;

const SpinnerMessage = styled.span`
  position: absolute;
  display: block;
  top: calc(100% + 20px);
  left: 50%;
  transform: translateX(-50%);

  white-space: nowrap;
  text-transform: uppercase;
  font-size: 10px;

  opacity: ${props => (props.visible ? 1 : 0)};

  transition-property: opacity;
  transition-duration: 0.4s;
  transition-timing-function: ease-in-out;
`;

const Spinner = ({
  messages,
  messageDelay,
  instantInitialMessage,
  children,
  style,
  messageStyle,
}) => {
  const initialIndex = useMemo(() => {
    if (messageDelay === 0 || instantInitialMessage) {
      return 0;
    }

    return -1;
  }, [messageDelay, instantInitialMessage]);

  const delayRef = useRef(0);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(initialIndex);

  const maxMessageIndex =
    messages && messages.length > 0 ? messages.length - 1 : 0;

  const incrementCurrentMessage = useCallback(() => {
    setCurrentMessageIndex(previous => previous + 1);

    if (currentMessageIndex + 1 < maxMessageIndex) {
      delayRef.current = setTimeout(
        incrementCurrentMessage,
        messageDelay * 1000,
      );
    }
  }, [currentMessageIndex, maxMessageIndex]);

  useEffect(() => {
    if (messageDelay > 0 && currentMessageIndex < maxMessageIndex) {
      delayRef.current = setTimeout(
        incrementCurrentMessage,
        messageDelay * 1000,
      );
    }

    return () => clearTimeout(delayRef.current);
  }, [messageDelay, currentMessageIndex]);

  return (
    <SpinnerElement style={style}>
      {children && !messages && (
        <SpinnerMessage
          visible={currentMessageIndex === 0}
          style={messageStyle}
        >
          {children}
        </SpinnerMessage>
      )}

      {messages &&
        messages.map((msg, index) => (
          <SpinnerMessage
            key={[msg, index].join('__')}
            visible={currentMessageIndex === index}
            style={messageStyle}
          >
            {msg}
          </SpinnerMessage>
        ))}
    </SpinnerElement>
  );
};

Spinner.propTypes = {
  messageDelay: PropTypes.number,
  messages: PropTypes.arrayOf(PropTypes.string),
  instantInitialMessage: PropTypes.bool,
  children: PropTypes.node,
  style: PropTypes.objectOf(PropTypes.any),
  messageStyle: PropTypes.objectOf(PropTypes.any),
};

Spinner.defaultProps = {
  messageDelay: 0,
  messages: null,
  instantInitialMessage: false,
  children: null,
  style: {},
  messageStyle: {},
};

export default Spinner;
